import { ApolloClient } from "@apollo/client";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { addDays } from "date-fns";

import type { EventModalEvent } from "@/components/calendar/EventModal";
import type { ValuesType } from "@/components/mails/Form";
import { MutateContactDocument } from "@/gql/graphql";
import type { ContactStatusFragment } from "@/gql/graphql";
import { MutationError } from "@/handleError";
import type { AppThunk, RootState } from "@/store";
import { gqlDateTime } from "@/utils/dates";

export type ContactsHistoryTabType =
  "histories" | "questionnaire" | "customer-contacts" | "todos" | "mails" | "attachments" | "audit-log";

export type AdditionalInfoType = { itype: ContactInfoType; value: string };

type ContactShowReducerState = {
  showEventModal: boolean;
  event: Nullable<EventModalEvent>;
  showFeedbackModal: boolean;
  feedbackModalClosable: boolean;
  feedbackType: Nullable<EventFeedbackTypeType>;

  showSubContactModal: boolean;
  subContact: Nullable<ContactChild>;

  showCreateMail: boolean;
  createMailValues: Nilable<Partial<ValuesType>>;
  showAddHistoryModal: boolean;
  showAddCustomerContactModal: boolean;
  showAddTodoModal: boolean;

  showAddAttachmentModal: boolean;

  activeHistoryTab: ContactsHistoryTabType;

  showContactInfosModal: boolean;
  showTransferModal: boolean;
  showStatusModal: boolean;
  contact: Nullable<ContactFull>;
  additionalInfo: Nullable<AdditionalInfoType>;
};

const initialState: ContactShowReducerState = {
  showEventModal: false,
  showFeedbackModal: false,
  feedbackModalClosable: true,
  event: null,
  feedbackType: null,

  showSubContactModal: false,
  subContact: null,

  showCreateMail: false,
  createMailValues: null,
  showAddHistoryModal: false,
  showAddCustomerContactModal: false,
  showAddTodoModal: false,

  showAddAttachmentModal: false,

  showContactInfosModal: false,
  showTransferModal: false,
  showStatusModal: false,
  contact: null,
  additionalInfo: null,

  activeHistoryTab: "histories",
};

type ContactInfoActionType = PayloadAction<{
  show: boolean;
  contact: Nullable<ContactFull>;
  additionalInfo?: AdditionalInfoType;
}>;

const contactsShowSlice = createSlice({
  name: "contacts.show",
  initialState,
  reducers: {
    setEventInfos(
      state,
      action: PayloadAction<{ event: Nullable<EventModalEvent>; show: boolean; closable: boolean }>,
    ) {
      state.showEventModal = action.payload.show;
      state.event = action.payload.event;
      state.feedbackModalClosable = action.payload.closable;
    },
    setFeedbackInfos(
      state,
      action: PayloadAction<{ event: Nullable<EventModalEvent>; show: boolean; type: Nullable<EventFeedbackTypeType> }>,
    ) {
      state.showFeedbackModal = action.payload.show;
      state.event = action.payload.event;
      state.feedbackType = action.payload.type;
    },

    setSubcontactInfos(state, action: PayloadAction<{ show: boolean; contact: Nullable<ContactChild> }>) {
      state.showSubContactModal = action.payload.show;
      state.subContact = action.payload.contact;
    },

    setShowCreateMail(state, action: PayloadAction<boolean>) {
      state.showCreateMail = action.payload;
      state.createMailValues = null;
    },

    setShowCreateMailWithValues(state, action: PayloadAction<{ show: boolean; values: Nilable<Partial<ValuesType>> }>) {
      state.showCreateMail = action.payload.show;
      state.createMailValues = action.payload.values;
    },

    setShowAddHistoryModal(state, action: PayloadAction<boolean>) {
      state.showAddHistoryModal = action.payload;
    },

    setShowAddCustomerContactModal(state, action: PayloadAction<boolean>) {
      state.showAddCustomerContactModal = action.payload;
    },

    setShowAddTodoModal(state, action: PayloadAction<boolean>) {
      state.showAddTodoModal = action.payload;
    },

    setActiveHistoryTab(state, action: PayloadAction<ContactsHistoryTabType>) {
      state.activeHistoryTab = action.payload;
    },

    setShowAttachmentModal(state, action: PayloadAction<boolean>) {
      state.showAddAttachmentModal = action.payload;
    },

    setShowContactInfos(state, action: ContactInfoActionType) {
      state.showContactInfosModal = action.payload.show;
      state.contact = action.payload.contact;
      state.additionalInfo = action.payload.additionalInfo || null;
    },

    setShowTransferModal(state, action: PayloadAction<{ show: boolean; contact: Nullable<ContactFull> }>) {
      state.showTransferModal = action.payload.show;
      state.contact = action.payload.contact;
    },

    setShowStatusModal(state, action: PayloadAction<{ show: boolean; contact: Nullable<ContactFull> }>) {
      state.showStatusModal = action.payload.show;
      state.contact = action.payload.contact;
    },
  },
});

export const {
  setEventInfos,
  setFeedbackInfos,
  setSubcontactInfos,
  setShowCreateMail,
  setShowAddHistoryModal,
  setShowAddCustomerContactModal,
  setShowAddTodoModal,
  setActiveHistoryTab,
  setShowAttachmentModal,
  setShowContactInfos,
  setShowTransferModal,
  setShowStatusModal,
  setShowCreateMailWithValues,
} = contactsShowSlice.actions;
export const contactsShowSelector = (state: RootState) => state.contacts.show;
export default contactsShowSlice.reducer;

type UpdateContactStatusType = (
  contact: ContactFull,
  status: ContactStatusFragment | undefined,
  client: ApolloClient,
  values?: Record<string, any>,
) => AppThunk;

export const updateContact: UpdateContactStatusType =
  (contact, status, client, values) => async (dispatch, getState) => {
    const { currentUser, currentProject, currentCustomer } = getState().session;

    const { data } = await client.mutate({
      mutation: MutateContactDocument,
      variables: {
        customerId: currentCustomer.id,
        projectId: currentProject.id,
        id: contact.id,
        contact: { ...(values || {}), statusId: status?.id },
      },
    });

    if (!data?.mutateContact) {
      throw new MutationError();
    }

    if (status?.enforceFollowup) {
      const startTime = addDays(new Date(), status.defaultFollowupDays || 0);

      dispatch(
        setEventInfos({
          show: true,
          closable: false,
          event: {
            typeId: status.followupEventTypeId || undefined,
            subject: status.defaultFollowupSubject || "",
            startTime: gqlDateTime(startTime),
            endTime: gqlDateTime(startTime),
            contactId: contact.id,
            ownerId: currentUser.id,
          },
        }),
      );
    }
  };
